/* eslint-disable no-param-reassign */
import UnicodeRegular from 'asset/font/DejaVuSans-Regular';
import UnicodeBold from 'asset/font/DejaVuSans-Bold';
import { Undo } from 'adapter/in/reports/utils/generatePDF';
import { processEmoji, rgbaToRgb } from 'adapter/in/reports/utils/reportRendering';

/**
 * Injects DejaVu Sans font-face CSS into the document if not already present.
 */
export const ensureTextBlockFontInjected = () => {
    const id = '__dejavu_sans_injected__';
    if (document.getElementById(id)) return;

    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
    @font-face {
        font-family: 'dejavusans';
        src: url(data:font/ttf;base64,${UnicodeRegular}) format('truetype');
        font-weight: 400;
        font-style: normal;
        font-display: swap;
    }
    @font-face {
        font-family: 'dejavusans';
        src: url(data:font/ttf;base64,${UnicodeBold}) format('truetype');
        font-weight: 700;
        font-style: normal;
        font-display: swap;
    }
`;
    document.head.appendChild(style);
};

/**
 * Loads the DejaVu Sans font into the browser font cache once.
 */
export const loadTextBlockFontOnce = async () => {
    const anyDoc = document as any;
    if (anyDoc.fonts && typeof anyDoc.fonts.load === 'function') {
        try {
            await Promise.all([anyDoc.fonts.load('400 12px dejavusans'), anyDoc.fonts.load('700 12px dejavusans')]);
        } catch {
            console.log('Failed to load DejaVu fonts');
        }
    }
};

/**
 * Converts all rgba background colors in the element's subtree to rgb, and processes emoji in text nodes.
 * Rgba colors can have issues in PDF rendering, i.e. with background colors.
 */
export const rgbaToRgbString = (element: HTMLElement) => {
    element.querySelectorAll('*').forEach((el) => {
        const cs = window.getComputedStyle(el as Element);
        const bg = cs.backgroundColor;
        if (bg.includes('rgba') && !bg.includes('0, 0, 0, 0)')) {
            (el as HTMLElement).style.backgroundColor = rgbaToRgb(bg);
        }
        if (el.childNodes?.length) {
            Array.from(el.childNodes)
                .filter((n) => n.nodeType === Node.TEXT_NODE)
                .forEach((n) => processEmoji.processTextNode(n));
        }
    });
};

/**
 * Normalizes list styles in the element's subtree for PDF rendering, and stores undo functions.
 * This is needed because html2canvas does not handle Tiptap list styles well.
 */
export const normalizeTextBlockLists = (element: HTMLElement, listUndos: Undo[]) => {
    element.querySelectorAll('ul, ol').forEach((listNode) => {
        const list = listNode as HTMLElement;
        const isOrdered = list.tagName.toLowerCase() === 'ol';

        const prevListStyle = {
            listStyle: list.style.listStyle,
            paddingLeft: list.style.paddingLeft,
            marginLeft: list.style.marginLeft,
        };
        list.style.listStyle = 'none';
        list.style.paddingLeft = '0';
        list.style.marginLeft = '0';
        listUndos.push(() => {
            list.style.listStyle = prevListStyle.listStyle;
            list.style.paddingLeft = prevListStyle.paddingLeft;
            list.style.marginLeft = prevListStyle.marginLeft;
        });

        Array.from(list.children)
            .filter((n): n is HTMLElement => n.tagName?.toLowerCase() === 'li')
            .forEach((_li, idx) => {
                const li = _li as HTMLElement;
                const originalChildren = Array.from(li.childNodes);

                const prevLi = {
                    display: li.style.display,
                    gap: li.style.gap,
                    alignItems: li.style.alignItems,
                    margin: li.style.margin,
                };

                li.style.display = 'flex';
                li.style.alignItems = 'baseline';
                li.style.gap = '0.4em';
                if (!li.style.margin) li.style.margin = '0 0 8px 0';

                const marker = document.createElement('span');
                marker.textContent = isOrdered ? `${idx + 1}.` : '•';
                marker.style.minWidth = isOrdered ? '1.4em' : '1.0em';
                marker.style.textAlign = 'right';
                marker.style.flex = '0 0 auto';

                const content = document.createElement('span');
                content.style.flex = '1 1 auto';
                content.style.display = 'inline';
                originalChildren.forEach((ch) => content.appendChild(ch));

                li.appendChild(marker);
                li.appendChild(content);

                listUndos.push(() => {
                    li.style.display = prevLi.display;
                    li.style.alignItems = prevLi.alignItems;
                    li.style.gap = prevLi.gap;
                    li.style.margin = prevLi.margin;

                    if (marker.parentNode === li) li.removeChild(marker);
                    if (content.parentNode === li) li.removeChild(content);

                    originalChildren.forEach((ch) => li.appendChild(ch));
                });
            });
    });
};

/**
 * Reverts the list normalization changes by executing stored undo functions.
 * We need to reverse the order of undos to restore the original state correctly,
 * ensuring the browser DOM remains exactly as it was before normalization.
 */
export const undoNormalizeTextBlockLists = (listUndos: Undo[]) => {
    listUndos.reverse().forEach((undo) => undo());
};
